import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import SiteOperationProgress from './SiteOperationProgress';
import { PHASES, phaseLabel, copyRoutine, routineChanged, stepKey, customiseRoutine, previewRoutine, validateDraft } from './sitePowerRoutineEditor';
import './SitePowerRoutines.css';

const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });
export default function SitePowerRoutines({ siteId, execution, externalBusy = false, onOpenPowerLog }) {
  const [data, setData] = useState(null);
  const [drafts, setDrafts] = useState({});
  const [phase, setPhase] = useState('startup');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [addType, setAddType] = useState('screen');
  const [assistantStatus, setAssistantStatus] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const mounted = useRef(false);
  const generation = useRef(0);
  const writing = useRef(false);
  const savedRef = useRef(null);
  const draftsRef = useRef({});
  const base = `/api/sites/${siteId}/power-routines`;
  const accept = useCallback((value, replacePhase) => {
    if (!value?.routines?.startup || !value?.routines?.shutdown || !Array.isArray(value.screens)) throw new Error('Site routines could not be loaded.');
    const next = Object.fromEntries(PHASES.map((name) => {
      const previous = draftsRef.current[name];
      const preserve = name !== replacePhase && routineChanged(previous, savedRef.current?.routines[name])
        && routineChanged(previous, value.routines[name]);
      return [name, preserve ? previous : copyRoutine(value.routines[name])];
    }));
    savedRef.current = value; draftsRef.current = next;
    setData(value); setDrafts(next);
  }, []);
  const load = useCallback(async (replacePhase) => {
    if (!siteId || writing.current) return;
    const version = ++generation.current;
    setLoading(true); setError(''); setMessage('');
    const [routineResult, statusResult] = await Promise.allSettled([
      axios.get(base, { headers: headers(), timeout: 10000 }),
      axios.get(`/api/googlehome/status?site=${siteId}`, { headers: headers(), timeout: 10000 }),
    ]);
    if (!mounted.current || generation.current !== version) return;
    try {
      if (routineResult.status === 'rejected') throw routineResult.reason;
      accept(routineResult.value.data, replacePhase);
      setAssistantStatus(statusResult.status === 'fulfilled' ? statusResult.value?.data || null : null);
      if (replacePhase) setMessage(`Saved ${phaseLabel(replacePhase).toLowerCase()} reloaded. Other unsaved edits were kept.`);
    } catch (failure) { setError(failure.response?.data?.message || failure.message); }
    finally { setLoading(false); }
  }, [siteId, base, accept]);
  useEffect(() => {
    mounted.current = true; writing.current = false;
    savedRef.current = null; draftsRef.current = {};
    setData(null); setDrafts({}); setPhase('startup'); setSaving(false); setAssistantStatus(null); setShowPreview(false);
    load();
    return () => { mounted.current = false; generation.current += 1; };
  }, [load]);
  const change = (routine) => {
    const next = { ...draftsRef.current, [phase]: routine };
    draftsRef.current = next; setDrafts(next); setMessage(''); setError('');
  };
  const draft = drafts[phase];
  const screens = data?.screens || [];
  const dirty = routineChanged(draft, data?.routines[phase]);
  const busy = saving || loading || externalBusy || execution?.busy;
  const preview = useMemo(() => previewRoutine(draft, data?.screens || [], phase), [draft, data, phase]);
  const omitted = screens.filter((screen) => screen.configured?.[phase] && !preview.some((step) => step.screenId === screen.id));
  const updateStep = (index, fields) => change({ ...draft, steps: draft.steps.map((step, position) => position === index ? { ...step, ...fields } : step) });
  const move = (index, direction) => {
    const steps = [...draft.steps];
    [steps[index], steps[index + direction]] = [steps[index + direction], steps[index]];
    change({ ...draft, steps });
  };
  const add = () => {
    if (busy || draft.steps.length >= 200 || (addType === 'remaining_screens' && draft.steps.some((step) => step.type === addType))) return;
    const step = { key: stepKey(), type: addType, ...(addType === 'screen' ? { screenId: null, areaId: null }
      : addType === 'delay' ? { seconds: 10 } : addType === 'assistant' ? { query: '' } : {}) };
    change({ ...draft, steps: [...draft.steps, step] });
  };
  const save = async (event) => {
    event.preventDefault();
    if (busy || writing.current) return;
    const validation = validateDraft(draft);
    if (validation) { setError(validation); return; }
    if (draft.steps.some((step) => step.type === 'screen' && !screens.some((screen) => screen.id === step.screenId && screen.areaId === step.areaId))) {
      setError('Replace or remove the missing Screen before saving.'); return;
    }
    const name = phase;
    const version = ++generation.current;
    writing.current = true; setSaving(true); setMessage(''); setError('');
    const value = { ...draft, steps: draft.steps.map((step) => step.type === 'assistant' ? { ...step, query: step.query.trim() } : { ...step }) };
    try {
      const response = await axios.put(`${base}/${name}`, value, { headers: headers(), timeout: 15000 });
      if (!mounted.current || generation.current !== version) return;
      accept(response.data, name);
      setMessage(`${phaseLabel(name)} saved. The next Site ${phaseLabel(name)} uses this routine.`);
    } catch (failure) {
      if (!mounted.current || generation.current !== version) return;
      setError(failure.response?.status === 409 ? 'This routine changed in another window. Your edits are kept. Reload this saved routine to review the current version.'
        : failure.response?.data?.message || failure.message);
    } finally {
      if (mounted.current && generation.current === version) { writing.current = false; setSaving(false); }
    }
  };
  if (!siteId) return <p>Select a site to configure Site routines.</p>;
  return <section className="site-power-routines" aria-label="Site routines">
    <header className="site-power-routines__hero">
      <div><span className="screen-power-workspace__eyebrow">All areas in this site</span><h2>Site routines{data?.siteName ? ` · ${data.siteName}` : ''}</h2>
        <p>Start or shut down the site in the order you choose. Individual failures are recorded and later steps continue.</p></div>
      <div className="site-power-routines__actions">
        {onOpenPowerLog && <button type="button" onClick={onOpenPowerLog}>View Power Log</button>}
        <button type="button" disabled={busy} onClick={() => load()}>Refresh routines</button>
      </div>
    </header>
    {loading && <p role="status">Loading site routines…</p>}
    {error && <p role="alert">{error}</p>}
    {message && <p role="status">{message}</p>}
    {data && draft && <>
      <nav className="site-power-routines__phases" aria-label="Choose site routine">{PHASES.map((name) => <button type="button" key={name}
        disabled={saving} aria-pressed={phase === name} onClick={() => { setPhase(name); setMessage(''); setError(''); setShowPreview(false); }}>
        {phaseLabel(name)}{routineChanged(drafts[name], data.routines[name]) ? ' · unsaved' : ''}</button>)}</nav>
      <div className="site-power-routines__summary"><strong>{draft.mode === 'default' ? 'Default behaviour' : 'Custom routine'}</strong>
        <span>{screens.filter((screen) => screen.configured?.[phase]).length} configured Screens across the site</span>
        {dirty && <span className="site-power-routines__unsaved">Unsaved changes · save before running</span>}</div>
      {draft.mode === 'default' && <div className="site-power-routines__default">
        <p>{phase === 'startup' ? 'Start' : 'Stop'} every configured Screen, with no extra delays. Screens without a saved {phase} routine are skipped.</p>
        <button type="button" disabled={busy} onClick={() => change(customiseRoutine(draft, screens, phase))}>Customise {phase}</button>
      </div>}
      <form onSubmit={save} aria-label={`${phaseLabel(phase)} routine editor`}>
        {draft.mode === 'custom' && <fieldset disabled={busy}><legend>{phaseLabel(phase)} sequence</legend>
          <ol className="site-power-routines__steps">{draft.steps.map((step, index) => <li key={step.key}>
            <span className="site-power-routines__number">{index + 1}</span>
            <div className="site-power-routines__step-fields">
              {step.type === 'screen' && <label>{phase === 'startup' ? 'Start' : 'Stop'} Screen
                <select aria-label={`Screen for step ${index + 1}`} required value={step.screenId || ''} onChange={(event) => {
                  const screen = screens.find((entry) => entry.id === Number(event.target.value));
                  updateStep(index, { screenId: screen?.id || null, areaId: screen?.areaId || null });
                }}><option value="">Choose an area and Screen</option>
                  {step.screenId && !screens.some((screen) => screen.id === step.screenId && screen.areaId === step.areaId) && <option value={step.screenId}>Missing Screen ({step.screenId})</option>}
                  {screens.map((screen) => <option key={screen.id} value={screen.id}
                    disabled={draft.steps.some((other, position) => position !== index && other.type === 'screen' && other.screenId === screen.id)}>
                    {screen.areaName} · {screen.name}{screen.configured?.[phase] ? '' : ' · not configured'}</option>)}
                </select></label>}
              {step.type === 'delay' && <label>Delay (seconds)<input aria-label={`Delay for step ${index + 1}`} type="number" required min="1" max="3600" step="1"
                value={step.seconds} onChange={(event) => updateStep(index, { seconds: event.target.value === '' ? '' : Number(event.target.value) })} /></label>}
              {step.type === 'assistant' && <label>Google Assistant phrase<input aria-label={`Assistant phrase for step ${index + 1}`} type="text" required maxLength="1000"
                value={step.query} placeholder="Lower the projector screen" onChange={(event) => updateStep(index, { query: event.target.value })} />
                <small>Use the exact name from Google Home. The reply will be recorded in Power Log.</small></label>}
              {step.type === 'remaining_screens' && <div><strong>{phase === 'startup' ? 'Start' : 'Stop'} remaining configured Screens</strong>
                <small>Includes newly configured Screens. Screens listed separately are excluded, wherever they appear in this sequence.</small></div>}
            </div>
            <div className="site-power-routines__step-actions">
              <button type="button" disabled={busy || index === 0} aria-label={`Move step ${index + 1} up`} onClick={() => move(index, -1)}>↑</button>
              <button type="button" disabled={busy || index === draft.steps.length - 1} aria-label={`Move step ${index + 1} down`} onClick={() => move(index, 1)}>↓</button>
              <button type="button" aria-label={`Remove step ${index + 1}`} onClick={() => change({ ...draft, steps: draft.steps.filter((_, position) => position !== index) })}>Remove</button>
            </div>
          </li>)}</ol>
          {!draft.steps.length && <p>This custom routine has no actions. Add a step or restore default behaviour.</p>}
          <div className="site-power-routines__add"><label>New step<select value={addType} onChange={(event) => setAddType(event.target.value)}>
            <option value="screen">{phase === 'startup' ? 'Start' : 'Stop'} a Screen</option><option value="remaining_screens">{phase === 'startup' ? 'Start' : 'Stop'} remaining Screens</option>
            <option value="delay">Delay</option><option value="assistant">Google Assistant command</option></select></label>
            <button type="button" disabled={busy || draft.steps.length >= 200 || (addType === 'remaining_screens' && draft.steps.some((step) => step.type === addType))} onClick={add}>Add step</button></div>
          {draft.steps.length >= 200 && <small>200 steps maximum. Remaining Screens can include additional Screens.</small>}
        </fieldset>}
        {draft.steps.some((step) => step.type === 'assistant') && <p className="site-power-routines__assistant">
          Google Assistant: {assistantStatus?.google_assistant ? 'Connected for this site' : assistantStatus?.google_assistant_needs_reauth ? 'Reconnect this site’s account in Settings → Integrations.'
            : assistantStatus ? 'Connect this site’s account in Settings → Integrations.' : 'Connection status unavailable. Check Settings → Integrations.'}</p>}
        <div className="site-power-routines__actions">
          <button type="submit" disabled={busy || !dirty}>{saving ? 'Saving…' : `Save ${phase}`}</button>
          {draft.mode === 'custom' && <button type="button" disabled={busy} onClick={() => change({ ...draft, mode: 'default', steps: [] })}>Restore default behaviour</button>}
          {dirty && <button type="button" disabled={busy} onClick={() => { change(copyRoutine(data.routines[phase])); }}>Discard changes</button>}
          <button type="button" disabled={busy} onClick={() => load(phase)}>Reload saved {phase}</button>
        </div>
      </form>
      <div className="site-power-routines__actions site-power-routines__run">
        <button type="button" disabled={busy || dirty || data.executionAvailable !== true} onClick={() => execution.run(phase)}>Run Site {phaseLabel(phase)}</button>
        <button type="button" aria-expanded={showPreview} onClick={() => setShowPreview(!showPreview)}>{showPreview ? 'Hide preview' : 'Preview steps'}</button>
      </div>
      {showPreview && <section className="site-power-routines__preview" aria-label={`${phaseLabel(phase)} sequence preview`}>
        <h3>{dirty ? 'Unsaved' : 'Saved'} {phase} preview</h3>
        <p>{draft.mode === 'default' ? 'All configured Screens in this site.' : 'Actions run in this order.'} Live results are recorded when you run the routine.</p>
        <ol>{preview.map((step) => <li key={step.key}><strong>{step.description}</strong>{step.reason && <small>{step.reason}</small>}</li>)}</ol>
        {!preview.length && <p>No actions to run.</p>}
        {omitted.length > 0 && <p>{omitted.length} configured Screens are omitted: {omitted.map((screen) => `${screen.areaName} · ${screen.name}`).join(', ')}. Add remaining Screens to include them.</p>}
      </section>}
    </>}
    <SiteOperationProgress {...execution} />
  </section>;
}
